import { Facility } from '~/types';
import { COUNTRIES, EMPTY_ADDRESS_FORM, STATES } from '../../../constants';
import { AddressFormValues, FacilityFormValues } from '../../types';
import {
  capitalizeEveryWord,
  hasAddressContent,
  stripLocationSuffix,
  transformCodeToSelectOption,
} from '../../utils';

export const formatFacilityAddress = (facility: Facility): AddressFormValues => {
  const stateCode = facility.address.stateCode;
  return {
    addressLine1: capitalizeEveryWord(facility.address.addressLine1),
    addressLine2: facility.address.addressLine2
      ? capitalizeEveryWord(facility.address.addressLine2)
      : '',
    city: capitalizeEveryWord(facility.address.city),
    postalCode: facility.address.postalCode,
    // [MCV-1374] Preserve non-US province codes. If the stored code isn't a
    // US state, pass it through as {value, label} so an async-selected Canadian
    // facility doesn't drop its province on the way into the form.
    stateCode: stateCode
      ? (STATES.find((s) => s.value === stateCode) ?? { value: stateCode, label: stateCode })
      : { value: '', label: '' },
    countryCode: transformCodeToSelectOption(facility.address.countryCode, COUNTRIES, {
      value: '',
      label: '',
    }),
  };
};

export const facilityFormToApi = (formFacility: FacilityFormValues) => {
  const formAddress = formFacility?.address;
  const addressFilled = hasAddressContent(formAddress);
  return JSON.stringify({
    // [MCV-1876] The label is "Name (CITY, ST)" picker chrome; saving it as-is would
    // store the location inside the catalog row's name. Match only the address that
    // is saved: a blank form still carries the default state, which is cleared below.
    name: addressFilled
      ? stripLocationSuffix(formFacility.label, formAddress?.city, formAddress?.stateCode?.value)
      : formFacility.label,
    userAddedFlag: true,
    address: {
      ...EMPTY_ADDRESS_FORM,
      ...formAddress,
      addressLine2: formAddress?.addressLine2 ? formAddress.addressLine2 : undefined,
      stateCode: addressFilled ? formAddress?.stateCode.value || '' : '',
      countryCode: addressFilled ? formAddress?.countryCode.value || '' : '',
    },
  } as Omit<Facility, 'id'>);
};

export const facilityApiToForm = (facilityStr: string): FacilityFormValues => {
  const facility: Facility = JSON.parse(facilityStr);
  const city = facility.address?.city;
  const state = facility.address?.stateCode;
  const locationSuffix = city || state ? ` (${[city, state].filter(Boolean).join(', ')})` : '';
  // [MCV-1876] Some catalog rows (mostly user-added) were saved with the picker's
  // " (City, ST)" baked into their name, sometimes repeated. Strip the facility's
  // own location first, or the suffix below is appended again.
  const name = facility.name ? stripLocationSuffix(facility.name, city, state) : facility.name;
  return {
    label: capitalizeEveryWord(name) + locationSuffix,
    rawName: name,
    value: facility.id.toString(),
    userAdded: facility.userAddedFlag,
    address: formatFacilityAddress(facility),
  };
};

export const facilitiesApiToForm = (facilitiesStr: string): FacilityFormValues[] => {
  const facilities: Facility[] = JSON.parse(facilitiesStr);
  return facilities.map((facility) => facilityApiToForm(JSON.stringify(facility)));
};
